import { isPageDrawn, type ComicPage, type GenState } from "@amzn/a4k-creator-studio-shared";

/** Zustand selector for whether the page currently being drawn has an image.
 *  Returns a primitive so subscribers don't re-render on unrelated page mutations. */
export function selectDrawingPageDone(st: { pages: ComicPage[]; currentDrawingPage: number }): boolean {
  const page = st.pages.find((p) => p.pageNumber === st.currentDrawingPage);
  return !!page && isPageDrawn(page);
}

export type GenPhase =
  | { kind: "creatingLayout" }
  | { kind: "drawingCover" }
  | { kind: "awaitingPages" }
  | { kind: "drawingPage"; page: number; total: number | null };

interface GenStatusInput {
  genState: GenState;
  currentDrawingPage: number;
  /** Whether the page currently being drawn already has an image. */
  drawingPageDone: boolean;
  layoutComplete: boolean;
  /** Authoritative total page count (cover included); null until layout completes. */
  totalPages: number | null;
}

/**
 * The current generation phase, derived from the pipelined server flow: the
 * cover is drawn first, then page layout streams in batches, so the total page
 * count is unknown until comicLayoutComplete reports it. Returns null when not
 * actively generating.
 */
export function getGenPhase({ genState, currentDrawingPage, drawingPageDone, layoutComplete, totalPages }: GenStatusInput): GenPhase | null {
  if (genState === "generating_prompts") return { kind: "creatingLayout" };
  if (genState !== "generating_pages") return null;

  if (currentDrawingPage <= 1 && !drawingPageDone) return { kind: "drawingCover" };
  if (!layoutComplete && drawingPageDone) return { kind: "awaitingPages" };
  if (currentDrawingPage > 1) {
    const total = layoutComplete && totalPages != null ? totalPages - 1 : null;
    return { kind: "drawingPage", page: currentDrawingPage - 1, total };
  }
  return { kind: "awaitingPages" };
}
